import styled, { keyframes } from 'styled-components'

import { ClinTheme } from '../../ClinTheme'
import { IClinAnnounceBarProps } from '../../components/ClinAnnounceBar/ClinAnnounceBar'
import { ClinButton } from '../../components/ClinButton'
import { ClinButtonIcon } from '../../components/ClinButtonIcon'
import { mediaQueryDown } from '../../utils/mediaQuery'
import { ClinText } from '../../components/ClinText'

interface IStyledIconProps {
  height?: number
  width?: number
}

interface IStyledDrugPropertiesContainerProps {
  unavailable?: boolean
}

const mobileUpBreakpoint = ClinTheme.breakpoints[0]
const desktopUpBreakpoint = ClinTheme.breakpoints[2]

const StyledSlideIn = keyframes`
0% {
  opacity: 0;
  transform: translateY(-${ClinTheme.space[3]}px);
}
100% {
   opacity: 1;
    transform: translateY(0px);
  }
`

export const StyledBasketDrawer = styled.div({
  padding: `0 ${ClinTheme.space[5]}px`,
  height: 'calc(100vh - 64px)',
  overflow: 'auto',
  [`.clin-announce`]: {
    marginBottom: ClinTheme.space[3]
  }
})

export const StyledDrawerHeader = styled.div({
  marginBottom: ClinTheme.space[3]
})

export const StyledDrawerBody = styled.div({})

export const Container = styled.div({
  width: '100%',
  overflow: 'hidden',
  boxSizing: 'border-box',
  display: 'grid',
  gridTemplateColumns: '34px 72px 3fr 1fr',
  gap: '16px',
  alignItems: 'center'
})

export const Row = styled.div({
  display: 'contents',
  boxSizing: 'border-box',
  width: '100%'
})

export const Header = styled(Row)({
  marginBottom: '16px',
  '& > div': {
    display: 'flex',
    alignItems: 'center',
    height: '48px'
  }
})

export const Column = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:last-child {
    min-width: 88px;
    text-align: right;
    justify-content: end;
  }
`

export const StyledClinSuccess = styled.div<IClinAnnounceBarProps>`
  display: flex;
  align-items: center;
  animation: ${StyledSlideIn} 0.15s ease-out;
  padding: 14px;
  border-radius: ${ClinTheme.radii[3]}px;
  background-color: ${ClinTheme.colors.lightenGreen};
  color: ${ClinTheme.colors.black};
  border: 0;
  margin-bottom: ${({ marginBottom }) => marginBottom ?? 0}px;
  word-break: break-word;
`

export const StyledClinInfo = styled.div<IClinAnnounceBarProps>`
  display: flex;
  align-items: center;
  animation: ${StyledSlideIn} 0.15s ease-out;
  padding: 14px;
  border-radius: ${ClinTheme.radii[3]}px;
  background-color: ${ClinTheme.colors.deliveryDateBackground};
  color: ${ClinTheme.colors.black};
  border: 0;
  margin-bottom: ${({ marginBottom }) => marginBottom ?? 0}px;
  word-break: break-word;
  justify-content: space-between;
`

export const StyledClinInfoIconBlock = styled.div<IClinAnnounceBarProps>`
  display: flex;
  align-items: center;
`

export const StyledIcon = styled.img<IStyledIconProps>(
  {
    objectFit: 'contain',
    objectPosition: 'left'
  },
  ({ height, width }: IStyledIconProps) => ({
    marginRight: '12px',
    height: height ? `${height}px` : '20px',
    width: width ? `${width}px` : '27px'
  })
)

export const StyledClinUndoButton = styled(ClinButton)({
  fontSize: ClinTheme.fontSizes[2],
  color: ClinTheme.colors.primaryLight,
  height: '24px',
  textDecoration: 'none'
})

export const StyledClinButton = styled(ClinButton)({
  fontSize: ClinTheme.fontSizes[2]
})

export const StyledClinButtonLink = styled(ClinButton)({
  height: 'auto',
  minWidth: '24px',
  whiteSpace: 'normal',
  overflowWrap: 'break-word',
  wordBreak: 'break-word',
  fontSize: ClinTheme.fontSizes[1],
  lineHeight: ClinTheme.lineHeights.heading[0],

  [mediaQueryDown(mobileUpBreakpoint)]: {
    wordBreak: 'break-word'
  }
})

export const StyledSpinnerContainer = styled.div({
  display: 'flex',
  height: '50vh',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  gap: '16px'
})

export const StyledClinButtonIcon = styled(ClinButtonIcon)({
  padding: '4px',
  backgroundColor: '#F0F1FA',

  '&:focus, &:hover': {
    boxShadow: 'none',
    backgroundColor: 'rgba(0,0,0,0.04)'
  }
})

export const StyledQuantitySelectorContainer =
  styled.div<IStyledDrugPropertiesContainerProps>(
    {
      display: 'flex',
      alignItems: 'center',
      boxSizing: 'border-box',
      cursor: 'default',
      minWidth: '72px',
      width: '72px',

      input: {
        padding: '8px',
        textAlign: 'right'
      },

      '.quantity-label > .clin-text': {
        fontSize: `${ClinTheme.fontSizes[0]}px`,
        lineHeight: `${ClinTheme.lineHeights.small}`
      },

      'input[type="number"]::-webkit-inner-spin-button': {
        marginLeft: '8px',
        display: 'inline-block !important',
        opacity: '1 !important'
      },

      'input[type="number"]::-webkit-outer-spin-button': {
        marginLeft: '8px',
        display: 'inline-block !important',
        opacity: '1 !important'
      }
    },
    ({ unavailable = false }: IStyledDrugPropertiesContainerProps) => ({
      opacity: unavailable ? 0.3 : 1
    })
  )

export const StyledFreeShippingContainer = styled.div({
  borderRadius: `${ClinTheme.radii[3]}px`,
  background: ClinTheme.colors.mintLight,
  height: '120px',
  padding: '20px',
  position: 'relative',
  boxSizing: 'border-box'
})

export const StyledShippingText = styled(ClinText)({
  fontFamily: ClinTheme.fonts.groteskRegular,
  position: 'absolute',
  right: '12px',
  bottom: '12px'
})

export const StyledShippingIcon = styled.img<IStyledIconProps>(
  {
    objectFit: 'contain',
    objectPosition: 'left'
  },
  ({ height, width }: IStyledIconProps) => ({
    height: height ? `${height}px` : '20px',
    width: width ? `${width}px` : '27px',

    [mediaQueryDown(desktopUpBreakpoint)]: {
      objectFit: 'fill',
      width: '100%'
    }
  })
)
